import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, ChevronDown, ChevronUp, Store, Users, HelpCircle } from 'lucide-react';

const FAQPage = ({ onBackToHome }) => {
  const [expandedCard, setExpandedCard] = useState(null);

  const toggleCard = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  const retailerFAQs = [
    {
      question: "How do I join Shoppers Discount Club as a retailer?",
      answer: "Simply click 'Request to be Listed' on our homepage, fill out the business application form with your details, and our team will review your application. Once approved, we'll set up your personalized AI chatbot and provide QR codes for your store.",
      tags: ["Getting Started", "Application"]
    },
    {
      question: "What pricing packages are available for retailers?",
      answer: "We offer four main packages: Standard (£13/month + £35 setup), Deluxe (£15/month + £35 setup), Platinum (£35/month + £35 setup), and Elite/Sponsor (custom pricing). Each package includes different features like AI chatbot, unique business URL, window stickers, and varying levels of updates and support.",
      tags: ["Pricing", "Packages"]
    },
    {
      question: "How does the QR code discount system work for my business?",
      answer: "We provide you with branded QR codes to display in your store window or at checkout. When customers scan these codes, they're directed to your personalized AI chatbot at [yourbusiness].shoppersdiscount.club, where they can access live discounts and offers specific to your store.",
      tags: ["QR Codes", "Technology"]
    },
    {
      question: "Can I customize the discounts and offers for my customers?",
      answer: "Yes! Your AI chatbot can be programmed with different offers for various customer segments (students, NHS workers, pensioners, etc.). You can update offers through your business dashboard, and we can provide 3-6 updates per month depending on your package.",
      tags: ["Customization", "Offers"]
    },
    {
      question: "What support do I get once I'm listed on the platform?",
      answer: "All packages include customer support, with Priority support for Deluxe+ packages and dedicated account managers for Platinum+ packages. We also provide analytics, performance reports, and technical assistance with your AI chatbot and QR code system.",
      tags: ["Support", "Analytics"]
    },
    {
      question: "Can I change my offers throughout the year?",
      answer: "If you subscribe to deluxe, platinum or elite you can request changes in accordance with the package you have ordered.",
      tags: ["Packages", "Offers"]
    }
  ];

  const shopperFAQs = [
    {
      question: "How do I find and use discounts on Shoppers Discount Club?",
      answer: "Browse our directory to find participating businesses near you, or scan QR codes displayed in store windows. Each business has a unique AI chatbot that provides instant, personalized discounts based on your profile (student, NHS worker, etc.).",
      tags: ["Getting Started", "Discounts"]
    },
    {
      question: "Do I need to create an account to access discounts?",
      answer: "No account required! Simply scan QR codes or visit business URLs to access instant discounts. However, creating a free account allows you to save favorite businesses, track your savings, and receive personalized offers.",
      tags: ["Account", "Access"]
    },
    {
      question: "Are the discounts really live and instant?",
      answer: "Yes! Our discounts are real-time and can be redeemed immediately at checkout. Each business can update their offers regularly, so you'll always see the most current deals available when you scan their QR code or visit their chatbot.",
      tags: ["Live Discounts", "Instant"]
    }
  ];

  const FAQCard = ({ faq, index, isExpanded, onToggle, category }) => (
    <Card className="mb-4 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader 
        className="cursor-pointer"
        onClick={() => onToggle(index)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold text-white ${
              category === 'retailer' ? 'bg-primary' : 'bg-orange'
            }`}>
              {index + 1}
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold text-charcoal text-left">
                {faq.question}
              </CardTitle>
              <div className="flex flex-wrap gap-2 mt-2">
                {faq.tags.map((tag, tagIndex) => (
                  <Badge key={tagIndex} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div className="ml-4">
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </div>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="pt-0">
          <div className="pl-11">
            <p className="text-gray-700 leading-relaxed">
              {faq.answer}
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-charcoal mb-2">
              Frequently Asked Questions
            </h1>
            <p className="text-gray-600 text-lg">
              Everything you need to know about Shoppers Discount Club
            </p>
          </div>
          <Button 
            onClick={onBackToHome}
            variant="outline"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>

        {/* Quick Navigation */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-charcoal mb-4">
            Quick Navigation
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a 
              href="#retailer-faqs" 
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
            >
              <Store className="h-6 w-6 text-primary" />
              <div>
                <h3 className="font-semibold text-charcoal">For Retailers</h3>
                <p className="text-gray-600 text-sm">6 questions about joining and managing your listing</p>
              </div>
            </a>
            <a 
              href="#shopper-faqs" 
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-orange hover:bg-orange/5 transition-colors"
            >
              <Users className="h-6 w-6 text-orange" />
              <div>
                <h3 className="font-semibold text-charcoal">For Shoppers</h3>
                <p className="text-gray-600 text-sm">3 questions about finding and using discounts</p>
              </div>
            </a>
          </div>
        </div>

        {/* Retailer FAQs */}
        <section id="retailer-faqs" className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Store className="h-8 w-8 text-primary" />
            <h2 className="text-3xl font-bold text-charcoal">
              For Retailers
            </h2>
            <Badge className="bg-primary text-white">
              6 Questions
            </Badge>
          </div>
          <div className="space-y-2">
            {retailerFAQs.map((faq, index) => (
              <FAQCard
                key={`retailer-${index}`}
                faq={faq}
                index={index}
                isExpanded={expandedCard === `retailer-${index}`}
                onToggle={() => toggleCard(`retailer-${index}`)}
                category="retailer"
              />
            ))}
          </div>
        </section>

        {/* Shopper FAQs */}
        <section id="shopper-faqs" className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Users className="h-8 w-8 text-orange" />
            <h2 className="text-3xl font-bold text-charcoal">
              For Shoppers
            </h2>
            <Badge className="bg-orange text-white">
              3 Questions
            </Badge>
          </div>
          <div className="space-y-2">
            {shopperFAQs.map((faq, index) => (
              <FAQCard
                key={`shopper-${index}`}
                faq={faq}
                index={index}
                isExpanded={expandedCard === `shopper-${index}`}
                onToggle={() => toggleCard(`shopper-${index}`)}
                category="shopper"
              />
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <div className="bg-gradient-to-br from-primary to-orange rounded-lg p-8 text-center text-white">
          <HelpCircle className="h-12 w-12 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">
            Still Need Help?
          </h2>
          <p className="text-lg mb-6 opacity-90">
            Can't find the answer you're looking for? Our support team is here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-primary hover:bg-gray-100"
              onClick={() => window.location.href = '/contact'}
            >
              Contact Support
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-primary"
              onClick={() => window.location.href = '/how-it-works'}
            >
              Watch Video Guides
            </Button>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="mt-12 text-center">
          <h3 className="text-xl font-semibold text-charcoal mb-4">
            Additional Resources
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/pricing'}
            >
              Pricing Guide
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/how-it-works'}
            >
              How It Works
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/terms-of-use'}
            >
              Terms of Use
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/privacy-policy'}
            >
              Privacy Policy
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;